// Centralized product data structure for PlugMonkey
// This file serves as the single source of truth for all product information
// Used across schemas, components, and pages

// Import all product data using clean ES6 imports
import { xUnfollowProProduct } from "./data/products/x-unfollow-pro";
import { videoDownloaderProProduct } from "./data/products/video-downloader-pro";
import { xFollowersExporterProProduct } from "./data/products/x-followers-exporter-pro";
import { promptAnythingProProduct } from "./data/products/prompt-anything-pro";
import { instagramUnfollowProProduct } from "./data/products/instagram-unfollow-pro";
import { instagramFollowersExporterProProduct } from "./data/products/instagram-followers-exporter-pro";
import { redditScoutProProduct } from "./data/products/reddit-scout-pro";
import { readmonkeyProProduct } from "./data/products/readmonkey-pro";
import { enhancedPdfViewerProduct } from "./data/products/enhanced-pdf-viewer";
import { linkedinConnectionRemoverProProduct } from "./data/products/linkedin-connection-remover-pro";
import type { DirectoryListing } from "./directory-listings";

/**
 * Product ID type - used throughout the codebase
 */
export type ProductId =
  | "x-unfollow-pro"
  | "x-followers-exporter-pro"
  | "video-downloader-pro"
  | "prompt-anything-pro"
  | "instagram-unfollow-pro"
  | "instagram-followers-exporter-pro"
  | "reddit-scout-pro"
  | "readmonkey-pro"
  | "enhanced-pdf-viewer"
  | "linkedin-connection-remover-pro";

/**
 * Offer configuration for a product
 */
export interface ProductOffer {
  name: string;
  description: string;
  price: string; // "0" for free, or price string like "49.99"
  priceCurrency: "USD";
  url: string; // Chrome store URL for free, purchase URL for pro
  priceValidUntil?: string; // ISO date string, e.g., "2030-12-31"
  warranty?: {
    durationOfWarranty: string; // ISO 8601 duration, e.g., "P30D"
    warrantyScope: string;
  };
}

/**
 * Aggregate rating information
 */
export interface ProductRating {
  ratingValue: string;
  reviewCount: string;
  bestRating: "5";
  worstRating: "1";
}

/**
 * HowTo schema step data
 */
export interface HowToStep {
  name: string;
  text: string;
  url?: string;
  position: number;
}

/**
 * HowTo schema configuration
 */
export interface ProductHowTo {
  name: string;
  description: string;
  totalTime: string; // ISO 8601 duration, e.g., "PT5M"
  tool: string;
  steps: HowToStep[];
}

/**
 * Feature block (icon + title + description)
 * Used for the main benefits section
 */
export interface ProductFeature {
  title: string;
  description: string;
  icon: string; // React-icons name matching dynamic icon loader
}

/**
 * Detailed feature section (alternating text/image or grid)
 */
export interface DetailedFeature {
  title: string;
  description: string;
  image?: string;
  bullets?: string[];
}

/**
 * FAQ Item
 */
export interface FAQItem {
  question: string;
  answer: string; // Can support simple HTML/Markdown
}

/**
 * Testimonial Item
 */
export interface Testimonial {
  text: string;
  author: string;
  role?: string; // e.g., "Social Media Manager"
  rating: 1 | 2 | 3 | 4 | 5;
  avatar?: string;
}

/**
 * Hero Section Configuration
 */
export interface HeroData {
  headline: string; // Main H1 (can include HTML span for color)
  subheadline: string; // H2/Subtitle
  ctaText: string;
  badgeText?: string; // e.g., "v2.0 • Last Updated Today"
  videoUrl?: string; // Demo video URL
}

/**
 * A curated cross-link from a product page to a related resource elsewhere on the site.
 * Used to surface high-quality content from /guide/, /download/, /best/, /faq/ to the
 * already-converting product-page traffic. Each product hand-picks 4-6 of these.
 */
export interface ProductRelatedResource {
  category: "guide" | "download" | "best" | "faq" | "comparison" | "alternative";
  slug: string; // e.g., "how-to-bulk-unfollow-on-x" — combined with category to form the URL
  title: string; // Display title — typically the same as the target page's H1 or a shorter version
  description?: string; // Optional 1-line context (e.g., "Step-by-step workflow with safety tips")
}

/**
 * Complete product data structure
 */
export interface Product {
  // Basic identification
  id: ProductId;
  slug: string; // URL slug, e.g., "x-unfollow-pro"
  name: string; // Full name, e.g., "PlugMonkey's Unfollow Pro for X (Twitter)"
  alternateName: string; // Short name, e.g., "Unfollow Pro for X"
  shortName: string; // Name without "Pro", e.g., "X Unfollow" (for pricing page product chooser)

  // URLs and paths
  url: string; // Full product page URL
  chromeStoreRedirect: string; // Redirect path for Chrome store

  // Descriptions
  description: string; // Full product description
  shortDescription?: string; // Short description for cards/previews

  // Search-result overrides, used ONLY for the product page's <title> and meta
  // description (plus the matching Open Graph / Twitter fields). Set these when
  // `name` / `description` exceed what results display (60 / 160 characters).
  // `name` and `description` also feed cards, schema, and analytics (including
  // LemonSqueezy purchase matching), so they are not shortened in place.
  metaTitle?: string; // <= 60 characters
  metaDescription?: string; // <= 160 characters

  // Visual
  image: string[]; // Array of image URLs
  icon: {
    name: string; // Icon name for DynamicIcon (e.g., "HiSparkles")
    backgroundClasses?: string; // CSS classes for icon background (e.g., "bg-primary/10")
    color?: string; // Icon color (hex code, e.g., "#E1306C" for Instagram)
  };

  // Categorization
  category: string; // Schema.org category path
  applicationCategory: "BrowserApplication";
  operatingSystem: "Chrome";

  // Audience
  audienceType: string; // e.g., "Social Media Users"

  // Offers
  freeOffer: ProductOffer;

  // Tiered pricing offers (monthly, yearly, lifetime)
  offers?: {
    monthly: ProductOffer;
    yearly: ProductOffer;
    lifetime: ProductOffer;
  };

  // Bundle offer (typically shared across all products but defined here for schema convenience)
  bundleOffer?: ProductOffer;

  // Ratings
  aggregateRating: ProductRating;

  // User/Install count (separate from review count)
  userCount?: string; // e.g., "1.09K", "109", "56"

  // Keywords for SEO
  keywords: string;

  // Additional properties for schema
  additionalProperties?: Array<{
    name: string;
    value: string;
  }>;

  // Version info
  version: string;

  // UI Sections
  hero: HeroData;
  features: ProductFeature[]; // Top-level benefits (usually 3-4)
  longFormFeatures: DetailedFeature[]; // In-depth "What it does" sections
  faq: FAQItem[];
  testimonials: Testimonial[];

  /**
   * Hand-picked cross-links surfaced on the product page (between FAQ and Final CTA).
   * Routes high-converting product-page traffic to deeper content (guides, downloads, roundups,
   * standalone FAQs, comparisons, alternatives). Each product gets 4-6.
   * Optional for backward compatibility with products that haven't been populated yet.
   */
  relatedResources?: ProductRelatedResource[];

  // HowTo schema data
  howTo: ProductHowTo; // Used for both Schema and "How it Works" visual section

  // Use Cases
  useCases?: Array<{
    title: string;
    description: string;
  }>;

  // Bottom CTA Section
  ctaSection?: {
    title: string;
    subtitle: string;
  };

  // Social Proof
  productHunt?: {
    slug: string; // e.g., "plugmonkey-s-xunfollow-pro"
    id: string; // ProductHunt ID if needed for embed, or just use slug
    title: string;
    rating: string;
    reviewCount: string;
  };
  // Launch directories listing this product; badges render in the hero.
  directoryListings?: DirectoryListing[];

  // Phase 6: Rich Modules for CRO
  demoSection?: {
    title: string;
    description: string;
    slides: {
      type: "image" | "video";
      src: string;
      poster?: string; // For video
      alt?: string;
    }[];
  };

  technicalSpecs?: {
    title: string;
    columns: number; // 1 or 2
    fields: {
      icon: string; // DynamicIcon name
      label: string;
      value?: string; // Optional detail text
    }[];
  };

  supportedPlatforms?: {
    title: string;
    platforms: {
      name: string;
      icon: string; // DynamicIcon name (e.g. FaTwitter)
    }[];
  };

  richFeatures?: {
    title: string;
    description: string;
    items: {
      icon: string;
      title: string;
      description: string;
    }[];
  };

  // Display order (for listing pages)
  displayOrder: number;

  // Header order priority (higher = shown first in header, top 3 on desktop xl viewport)
  headerOrder: number;

  // Coming-soon flag: product is registered (so the marketing page exists) but
  // awaiting Chrome Web Store approval. When true:
  //   • install / buy / pricing / install / uninstall / thank-you routes are
  //     skipped at build time (generateStaticParams filters these out)
  //   • sitemap-products.xml excludes the slug (don't index thin pages)
  //   • product cards render with a "Coming Soon" badge + Learn More CTA
  //   • ProductPageLayout swaps install/buy CTAs for a single Notify CTA
  //   • bundle math + llms.txt + ai-dataset.json should exclude these until
  //     the flag flips (handled by getLiveProducts() helper)
  comingSoon?: boolean;

  // Homepage card configuration (optional - if not provided, product won't appear on homepage)
  homepageCard?: {
    backgroundIconName: string; // Icon name for background decoration
    backgroundIconOpacity?: string; // CSS classes for background opacity
    backgroundIconTransform?: string; // CSS transform classes for background icon
    backgroundIconColor?: string; // Optional color class for background icon
    features: Array<{ label: string; value: string }>; // Feature highlights for homepage
    shortDescription?: string; // Optional shorter description for homepage (falls back to product.description)
  };

  // Pricing page specific data
  pricing: {
    tagline: string; // Marketing tagline for pricing page
    freeFeatures: string[]; // List of free tier features
    proFeatures: string[]; // List of pro tier features
    useCase: string; // Use case description for pricing page
  };

  // Extension flow data (Install/Uninstall)
  flowData?: {
    installation: {
      headline: string;
      subheadline: string;
      guideSteps: Array<{
        title: string;
        description: string;
        image?: string; // Optional generic image/icon reference
        screenshot?: string; // raw PNG filename relative to /product/[slug]/ (e.g., "screenshot-1.png"); next-image-export-optimizer generates the responsive WEBP set at build time
        ctaText?: string; // button text (e.g., "Open X.com →")
        ctaUrl?: string; // button URL (opens in new tab)
      }>;
      primaryCtaText?: string; // main CTA after steps (e.g., "Open X.com and Start Unfollowing →")
      primaryCtaUrl?: string; // main CTA URL
      emailCapture: {
        headline: string;
        description: string;
        buttonText: string;
      };
      demoVideo?: {
        headline: string; // e.g., "Test it right now"
        description: string; // e.g., "Download this demo video to see your extension in action"
        videoUrl: string; // Direct video URL (MP4 preferred)
        videoUrlWebm?: string; // WebM alternative for better compression
        posterUrl?: string; // Thumbnail/poster image URL
        videoDuration?: string; // e.g., "15s" for display
        videoResolution?: string; // e.g., "4K" or "1080p"
        ctaText?: string; // e.g., "Try downloading this video"
        attribution?: {
          // Required for proper licensing credit
          source: string; // e.g., "Pexels" or "Pixabay"
          authorName?: string; // Creator name
          authorUrl?: string; // Link to creator profile
          videoUrl?: string; // Link to original video page
        };
      };
    };
    uninstallation: {
      headline: string;
      subheadline: string;
      survey: {
        question: string;
        options: string[];
      };
    };
  };
}

/**
 * All PlugMonkey products
 * This is the single source of truth for product data
 */
/**
 * Legacy PRODUCTS array for backward compatibility
 * @deprecated Use getAllProducts() or getProductById() instead
 */
export const PRODUCTS: Product[] = getAllProducts();

/**
 * Get a product by its ID
 */
export function getProductById(id: ProductId): Product | undefined {
  return getProductData(id);
}

/**
 * Get pricing-specific data for a product
 * This is a convenience function that extracts pricing data from the Product interface
 * Use this in pricing pages to ensure data consistency
 */
export function getPricingDataForProduct(id: ProductId) {
  const product = getProductById(id);
  if (!product) {
    return undefined;
  }

  return {
    name: product.alternateName,
    tagline: product.pricing.tagline,
    description: product.description,
    freeFeatures: product.pricing.freeFeatures,
    proFeatures: product.pricing.proFeatures,
    reviews: `${product.aggregateRating.ratingValue}/5 (${product.aggregateRating.reviewCount} reviews)`,
    useCase: product.pricing.useCase,
    offers: product.offers,
  };
}

/**
 * Get a product by its slug
 */
export function getProductBySlug(slug: string): Product | undefined {
  return getAllProducts().find((product) => product.slug === slug);
}

/**
 * Get only the live products (excludes coming-soon / awaiting CWS approval).
 * Use this for: sitemap inclusion, bundle math, llms.txt, ai-dataset.json —
 * anywhere thin/unfinished pages or unshipped value would be misleading.
 */
export function getLiveProducts(): Product[] {
  return getAllProducts().filter((product) => !product.comingSoon);
}

/**
 * Get all products sorted by display order
 */
export function getAllProducts(): Product[] {
  // Dynamically import all product data to keep products.ts light
  const productIds: ProductId[] = [
    "x-unfollow-pro",
    "x-followers-exporter-pro",
    "video-downloader-pro",
    "prompt-anything-pro",
    "instagram-unfollow-pro",
    "instagram-followers-exporter-pro",
    "reddit-scout-pro",
    "readmonkey-pro",
    "enhanced-pdf-viewer",
    "linkedin-connection-remover-pro",
  ];
  const products = productIds
    .map((id) => getProductData(id))
    .filter((product): product is Product => product !== undefined);
  return products.sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get product URLs for use in schemas (e.g., significantLink, relatedLink)
 */
export function getProductUrls(): string[] {
  return getAllProducts().map((product) => product.url);
}

/**
 * Extract product ID from a pathname.
 * Supports paths: /product/{slug}/, /install/{slug}/, /uninstall/{slug}/, /pricing/{slug}/
 * @param pathname - The pathname to extract product from (e.g., "/product/x-unfollow-pro/" or "/pricing/instagram-unfollow-pro/")
 * @returns The product ID if found, null otherwise
 */
export function getProductIdFromPathname(pathname: string): ProductId | null {
  // Normalize pathname: ensure it has trailing slash (except root)
  const normalizedPath = pathname === "/" ? "/" : pathname.replace(/\/$/, "") + "/";

  // Check if pathname matches one of the product-related routes
  const productRoutePrefixes = ["/product/", "/install/", "/uninstall/", "/pricing/"];
  const matchingPrefix = productRoutePrefixes.find((prefix) => normalizedPath.startsWith(prefix));

  if (!matchingPrefix) {
    return null;
  }

  // Extract the slug from the pathname (everything after the prefix, before the trailing slash)
  const slug = normalizedPath.slice(matchingPrefix.length, -1);

  // Find product by slug using getAllProducts() for dynamic lookup
  const product = getAllProducts().find((p) => p.slug === slug);

  return product ? (product.id as ProductId) : null;
}

// Re-export business schema functions for backward compatibility
export { generateMakesOfferForBusiness } from "./business/business-schemas";

/**
 * Get product data by dynamically importing from data files
 * This keeps products.ts light by not including the actual product content
 */
function getProductData(productId: ProductId): Product | undefined {
  // Use ES6 imports to keep product data separate and products.ts clean
  switch (productId) {
    case "x-unfollow-pro":
      return xUnfollowProProduct;
    case "video-downloader-pro":
      return videoDownloaderProProduct;
    case "x-followers-exporter-pro":
      return xFollowersExporterProProduct;
    case "prompt-anything-pro":
      return promptAnythingProProduct;
    case "instagram-unfollow-pro":
      return instagramUnfollowProProduct;
    case "instagram-followers-exporter-pro":
      return instagramFollowersExporterProProduct;
    case "reddit-scout-pro":
      return redditScoutProProduct;
    case "readmonkey-pro":
      return readmonkeyProProduct;
    case "enhanced-pdf-viewer":
      return enhancedPdfViewerProduct;
    case "linkedin-connection-remover-pro":
      return linkedinConnectionRemoverProProduct;
    default:
      return undefined;
  }
}
